import { NavLink } from "react-router-dom";
import {
  CAPABILITY_LABEL,
  routeFor,
  routeTranslation,
  type AiCapability,
  type AiProviderDef,
  type AiProviderId,
  type AiSettings,
  type CapabilityChoice,
  type LlmChoice,
} from "@gk/shared";

export type ProviderStatusMap = Record<string, Record<string, { ok: boolean; reason?: string }>>;

export function AdminTabs() {
  return (
    <nav className="tabs" aria-label="Admin panel">
      <NavLink to="/admin/ai">AI providers</NavLink>
      <NavLink to="/admin/clients">Clients</NavLink>
      <NavLink to="/admin/users">Users</NavLink>
      <NavLink to="/admin/security">Security and audit</NavLink>
    </nav>
  );
}

const VENDOR_SHORT: Record<AiProviderId, string> = {
  google: "Google",
  aws: "AWS",
  azure: "Azure",
  anthropic: "Anthropic",
  openai: "OpenAI",
  mock: "Simulated",
};

export function VendorChip({ id, muted }: { id: AiProviderId; muted?: boolean }) {
  return <span className={`vendor v-${id}${muted ? " muted-chip" : ""}`}>{VENDOR_SHORT[id]}</span>;
}

export const usd = (n: number) => (n < 0.01 && n > 0 ? "< $0.01" : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/** Options for one capability: every provider that offers it, marked when not set up yet. */
function ProviderSelect({
  capability,
  value,
  onChange,
  providers,
  status,
  allowNone,
  label,
  disabled,
}: {
  capability: AiCapability;
  value: AiProviderId | null;
  onChange: (v: AiProviderId | null) => void;
  providers: AiProviderDef[];
  status: ProviderStatusMap;
  allowNone?: boolean;
  label: string;
  disabled?: boolean;
}) {
  return (
    <select className="input" aria-label={label} disabled={disabled} value={value ?? ""} onChange={(e) => onChange((e.target.value || null) as AiProviderId | null)}>
      {allowNone && <option value="">None</option>}
      {providers
        .filter((p) => p.capabilities.includes(capability))
        .map((p) => {
          const st = status[p.id]?.[capability];
          return (
            <option key={p.id} value={p.id} disabled={!st?.ok && p.id !== value}>
              {p.name}
              {st && !st.ok ? ` — ${st.reason}` : ""}
            </option>
          );
        })}
    </select>
  );
}

/**
 * The drop-downs. In a client's override, each capability can follow the
 * platform default (shown) or use its own choice.
 */
export function AiChoiceEditor({
  value,
  onChange,
  providers,
  status,
  inherit,
}: {
  value: AiSettings;
  onChange: (v: AiSettings) => void;
  providers: AiProviderDef[];
  status: ProviderStatusMap;
  /** Override mode: which capabilities follow the platform, and what the platform uses. */
  inherit?: { platform: AiSettings; following: Record<AiCapability, boolean>; onFollowing: (c: AiCapability, follow: boolean) => void };
}) {
  const speech = ["stt", "translate", "tts"] as const;
  const setChoice = (c: (typeof speech)[number], patch: Partial<CapabilityChoice>) => onChange({ ...value, [c]: { ...value[c], ...patch } });
  const setLlm = (patch: Partial<LlmChoice>) => onChange({ ...value, llm: { ...value.llm, ...patch } });
  const followToggle = (c: AiCapability) =>
    inherit && (
      <label className="row small" style={{ gap: 6 }}>
        <input type="checkbox" checked={inherit.following[c]} onChange={(e) => inherit.onFollowing(c, e.target.checked)} />
        Platform default
      </label>
    );

  return (
    <div className="choice-grid" role="group" aria-label="AI providers">
      <div className="choice-head">Service</div>
      <div className="choice-head">Provider</div>
      <div className="choice-head">Fallback / models</div>
      {speech.map((c) => {
        const follow = inherit?.following[c];
        const v = follow ? inherit!.platform[c] : value[c];
        return (
          <div className="choice-row" key={c}>
            <div>
              <b>{CAPABILITY_LABEL[c]}</b>
              {followToggle(c)}
            </div>
            <ProviderSelect
              capability={c}
              label={`${CAPABILITY_LABEL[c]} provider`}
              value={v.provider}
              onChange={(p) => p && setChoice(c, { provider: p, fallback: v.fallback === p ? null : v.fallback })}
              providers={providers}
              status={status}
              disabled={follow}
            />
            <div>
              <ProviderSelect
                capability={c}
                label={`${CAPABILITY_LABEL[c]} fallback`}
                value={v.fallback}
                onChange={(p) => setChoice(c, { fallback: p === v.provider ? null : p })}
                providers={providers}
                status={status}
                allowNone
                disabled={follow}
              />
              <div className="xs muted" style={{ marginTop: 4 }}>
                Used for languages the provider does not offer
              </div>
            </div>
          </div>
        );
      })}
      {(() => {
        const follow = inherit?.following.llm;
        const v = follow ? inherit!.platform.llm : value.llm;
        const def = providers.find((p) => p.id === v.provider);
        return (
          <div className="choice-row">
            <div>
              <b>{CAPABILITY_LABEL.llm}</b>
              {followToggle("llm")}
            </div>
            <ProviderSelect
              capability="llm"
              label="Language model provider"
              value={v.provider}
              onChange={(p) => {
                if (!p) return;
                const d = providers.find((x) => x.id === p)?.llmDefaults;
                setLlm({ provider: p, analysisModel: d?.analysis || "", liveModel: d?.live || "" });
              }}
              providers={providers}
              status={status}
              disabled={follow}
            />
            <div className="stack" style={{ gap: 6 }}>
              <label className="field">
                <span className="xs">Analysis and reports (most capable)</span>
                <input className="input" disabled={follow} value={v.analysisModel} onChange={(e) => setLlm({ analysisModel: e.target.value })} placeholder="model or deployment" />
              </label>
              <label className="field">
                <span className="xs">Live suggestions (fast)</span>
                <input className="input" disabled={follow} value={v.liveModel} onChange={(e) => setLlm({ liveModel: e.target.value })} placeholder="model or deployment" />
              </label>
              {def?.llmDefaults?.help && <div className="xs muted">{def.llmDefaults.help}</div>}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/** Which vendor will actually serve each language, given the choices on screen. */
export function ServeGrid({ settings, languages }: { settings: AiSettings; languages: Array<{ code: string; name: string }> }) {
  const caps = ["stt", "translate", "tts"] as const;
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="serve-grid">
        <thead>
          <tr>
            <th scope="col">Language</th>
            {caps.map((c) => (
              <th key={c} scope="col">
                {CAPABILITY_LABEL[c].replace(" (text to speech)", "")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {languages.map((l) => (
            <tr key={l.code}>
              <th scope="row">{l.name}</th>
              {caps.map((c) => {
                const p = c === "translate" ? routeTranslation(settings.translate, l.code, "en-US") : routeFor(settings[c], c, l.code);
                const viaFallback = p && p !== settings[c].provider;
                return (
                  <td key={c} className={p ? "" : "gap"}>
                    {p ? (
                      <>
                        <VendorChip id={p} />
                        {viaFallback && <span className="xs muted"> fallback</span>}
                      </>
                    ) : (
                      <span className="gap-label">Not covered</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
